import React, { useState, useEffect } from 'react';
import moment from 'moment';

import { Container } from 'react-bootstrap';

import ParentHeader from '../ParentHeader/ParentHeader';
import EventFeed from '../../Components/EventFeed/EventFeed';
import EntryCard from '../../Components/EntryCard/EntryCard';
import { cardActions, entriesFrom, notEnteredIn, useCompetitionCounts, useFamilyDancers } from '../../Components/EntryCard/entryCardModel';
import Help from '../../Components/Help/Help';

import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Loader from '../../Components/Loader/Loader';
import { serviceConsumer } from '../../network/ServiceConsumer';

import './Dashboard.css';
import { postDownloadHandler } from '../../helper/printHandlers';
import { eventDateOf, isEventToday } from '../../helper/eventFeed';

// Families rarely hold more than a handful of upcoming entries; they all load
// at once, soonest first, above the event feed (no pagination).
const ENTERED_LIMIT = 100;

// One family entry card per event (all its dancers), the same card as on the
// event page (MC2 ecbc86f7); it reads its own competition counts once they open.
const DashboardEntry = ({ group, family, onOpen, onChange, onPrint, go }) => {
  const { event, kind, items } = group;
  const counts = useCompetitionCounts(kind === 'feis' ? event?._id : null, kind === 'feis');
  const entries = entriesFrom(items, { kind, event, onChange, onPrint: kind === 'feis' ? onPrint : null });
  const actions = cardActions({
    event,
    kind,
    notEntered: notEnteredIn(family, items),
    isTeacher: localStorage.getItem('isTeacher') === 'true',
    go: {
      counts: go.counts,
      open: (url) => window.open(url, '_blank', 'noopener,noreferrer'),
      add: go.add,
      results: go.results,
    },
  });
  const today = isEventToday(kind === 'grade' ? { ...event, eventType: 'Grade Exam' } : event);
  return <EntryCard event={event} kind={kind} showEvent today={today} onOpen={onOpen} entries={entries} counts={counts} actions={actions} />;
};

const entryDate = (item) =>
  new Date(item?.event_type === 'grade' ? item?.result?.exam_date : eventDateOf(item?.result) || 0);

export const ParentDashboard = () => {
  const [registeredDancers, setRegisteredDancers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [, setErr] = useState('');
  const payload = useSelector((state) => state.authReducer);

  const navigate = useNavigate();
  const family = useFamilyDancers();

  // View event detail page
  const editPage = (item) => {
    const urlEventName = item?.result?.name?.split(' ').join('+');
    if (item.event_type === 'grade') {
      navigate(`/edit-grade/${urlEventName}?id=${item.result._id}`, { state: { eventProp: item } });
    } else {
      navigate(`/edit-feis/${urlEventName}?id=${item.result._id}`, { state: { eventProp: item } });
    }
  };

  // On the day of a feis the family entered, the card opens its results
  // (Devin 2026-09-25: ~95% of event-day taps are for results); editing the
  // registration stays one button away.
  const viewResults = (item) => {
    const urlEventName = item?.result?.name?.split(' ').join('+');
    navigate(`/results/${urlEventName}?id=${item.result._id}`, { state: { event: item.result, from: 'result' } });
  };

  // Print Number Card - directly generate PDF
  const printCard = (item) => {
    const urlApi = `${process.env.REACT_APP_BASE_URL}/registration/printcard/pdf`;
    const body = { registrationId: item._id, type: 'one' };
    const dancerName = item?.dancerData?.fname || 'NumberCard';
    postDownloadHandler(urlApi, body, setLoading, setErr, `${dancerName}-NumberCard.pdf`);
  };

  // Edit registration - go directly to dancer edit page
  const editRegistration = (item) => {
    if (item.event_type === 'grade') {
      navigate(`/grade-edit-dancers/${item.result._id}?regId=${item._id}`, { state: { event: item.result } });
    } else {
      navigate(`/feis-edit-dancers/${item.result._id}?regId=${item._id}`, { state: { event: item.result } });
    }
  };

  //get details of all registered dancers
  const getAllEntered = async () => {
    const url = `${process.env.REACT_APP_BASE_URL}/feis/entered`;
    try {
      setLoading(true);
      // `date` = local wall clock labelled UTC (as the event listings send), so
      // the event day's card stays until the family's own midnight, not the
      // server's UTC midnight (8 pm EDT)
      const date = moment(new Date()).utc(true).format();
      const response = await serviceConsumer('POST', url, { page: 1, limit: ENTERED_LIMIT, date });
      const data = response?.Feis?.results || [];
      setRegisteredDancers([...data].sort((a, b) => entryDate(a) - entryDate(b)));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllEntered();
    document.title = 'Feis.link ';
  }, []);

  // the family's registrations grouped by event, soonest first (the list is sorted)
  const groups = [];
  const byEvent = new Map();
  registeredDancers.forEach((item) => {
    const id = item?.result?._id;
    if (!id) return;
    const key = `${item.event_type}:${id}`;
    if (!byEvent.has(key)) {
      const g = { key, event: item.result, kind: item.event_type === 'grade' ? 'grade' : 'feis', items: [] };
      byEvent.set(key, g);
      groups.push(g);
    }
    byEvent.get(key).items.push(item);
  });

  const entered = groups.length > 0 && (
    <div className="feed-section feed-section--entered">
      <div className="feed-heading" role="heading" aria-level="2">Entered</div>
      {groups.map((g) => {
        const urlEventName = g.event?.name?.split(' ').join('+');
        return (
          <DashboardEntry
            key={g.key}
            group={g}
            family={family}
            onOpen={() => editPage(g.items[0])}
            onChange={editRegistration}
            onPrint={printCard}
            go={{
              counts: () => navigate(`/competitors/${urlEventName}?id=${g.event._id}`, { state: { from: 'edit' } }),
              add: () => navigate(g.kind === 'grade' ? `/register-grade-exam?id=${g.event._id}` : `/register-dancers?id=${g.event._id}`, { state: { event: g.event } }),
              results: () => viewResults(g.items[0]),
            }}
          />
        );
      })}
    </div>
  );

  return (
    <div>
      <ParentHeader payload={payload} />
      {loading && <Loader />}
      <div className="pd-container">
        <Container style={{ marginBottom: '8%' }}>
          <EventFeed upcomingOnly searchHeading="Events" lead={entered} hideEventIds={registeredDancers.map((item) => item?.result?._id).filter(Boolean)} />
        </Container>
        <Help />
      </div>
    </div>
  );
};
